import { useMemo, useState } from "react";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { useClipboard } from "@/hooks/useClipboard";
import { accessLines, addressFilters } from "@/mocks/home";

const latencyTone: Record<number, string> = {
  1: "bg-secondary-100 text-secondary-700",
  2: "bg-primary-100 text-primary-700",
  3: "bg-accent-100 text-accent-700",
};

export default function AddressSection() {
  const [activeFilter, setActiveFilter] = useState("all");
  const { copiedKey, copy } = useClipboard();

  const visibleLines = useMemo(() => {
    if (activeFilter === "all") return accessLines;
    return accessLines.filter((line) => line.category === activeFilter);
  }, [activeFilter]);

  return (
    <section
      id="latest-address"
      className="scroll-mt-28 py-14 md:py-20 bg-background-100 border-y border-background-200"
    >
      <div className="px-4 md:px-6">
        <SectionHeading
          eyebrow="最新网址"
          title="蜜月TV最新可用地址汇总"
          desc="以下线路由人工核对整理，标注为主线路的地址优先使用；如遇访问异常，可在备用线路之间切换。"
        />

        <div className="mt-8 flex flex-wrap items-center gap-2">
          {addressFilters.map((filter) => {
            const active = filter.id === activeFilter;
            return (
              <button
                key={filter.id}
                type="button"
                onClick={() => setActiveFilter(filter.id)}
                aria-pressed={active}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                  active
                    ? "bg-primary-500 text-background-50 border-primary-500"
                    : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {filter.label}
              </button>
            );
          })}
          <span className="ml-auto text-xs text-foreground-500">
            共 {visibleLines.length} 条线路
          </span>
        </div>

        <ul className="mt-6 space-y-3">
          {visibleLines.map((line) => {
            const copied = copiedKey === line.id;
            return (
              <li
                key={line.id}
                className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 p-4 md:p-5 rounded-lg border border-background-200 bg-background-50"
              >
                <div className="flex items-center gap-3 md:w-44 shrink-0">
                  <span className="w-10 h-10 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className="ri-server-line text-lg" aria-hidden="true" />
                  </span>
                  <div>
                    <p className="font-heading font-bold text-sm text-foreground-950">{line.name}</p>
                    <p className="text-xs text-foreground-500">{line.categoryLabel}</p>
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <code className="block font-label text-sm text-foreground-800 truncate">
                    {line.url}
                  </code>
                  <p className="mt-1 text-xs text-foreground-500 leading-relaxed">{line.note}</p>
                </div>

                <div className="flex items-center gap-2 md:gap-3 shrink-0">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${latencyTone[line.latencyScore]}`}>
                    {line.latency}
                  </span>
                  {line.status === "推荐" ? (
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                      推荐
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => copy(line.url, line.id)}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                      copied
                        ? "bg-secondary-500 text-background-50 border-secondary-500"
                        : "bg-background-50 text-foreground-800 border-background-300 hover:border-primary-400 hover:text-primary-700"
                    }`}
                    aria-label={`复制 ${line.name} 地址`}
                  >
                    <i className={copied ? "ri-check-line" : "ri-file-copy-line"} aria-hidden="true" />
                    {copied ? "已复制" : "复制"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-6 flex items-start gap-3 p-4 rounded-lg bg-accent-100/70 border border-accent-200">
          <i className="ri-alert-line text-accent-700 text-lg mt-0.5" aria-hidden="true" />
          <p className="text-sm text-accent-900 leading-relaxed">
            复制地址后请在浏览器地址栏<strong className="font-semibold">完整粘贴</strong>再访问。
            若页面要求输入支付密码或安装不明插件，请立即关闭并更换线路，以保护账号安全。
          </p>
        </div>
      </div>
    </section>
  );
}